import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Container } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import { getCreditTypes, getCredits } from '~/services/PaymentsService';
import UsersService from '~/services/UsersService';

import { parseSearchString } from '~/utils/url';

import UserBreadcrumbs from '../../Common/Elements/UserBreadcrumbs';
import ErrorDisplay from '../../Common/ErrorDisplay';
import Spinner from '../../Common/Spinner';
import FraudWarning from '../History/FraudWarning';

import CreditsCurrencySelector from './CreditsCurrencySelector';
import CreditsPage from './CreditsPage';
import { CreditTypes, Credits } from './types';

type Props = RouteComponentProps<{ id_user: string }>;

const regions = libRegions.getRegions();

export default function CreditsContainer({ match, location }: Props) {
  const { id_user: userId } = match.params;
  const qsString = location.search;

  const brand = useSelector((state: App.State) => state.tenant.brand);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [creditTypes, setCreditTypes] = useState<CreditTypes>([]);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [user, setUser] = useState<App.User | null>(null);
  const [initiators, setInitiators] = useState([]);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const currency = useMemo<string>(() => {
    const { currency } = parseSearchString(qsString);
    return (currency as string) || 'AUD';
  }, [qsString]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const creditTypes = getCreditTypes();

      const [credits, user] = await Promise.all([
        getCredits(userId, currency, brand, currentPage, 50),
        UsersService.getUser(userId),
      ]);

      const initiators = await Promise.all(
        [...new Set(credits.result.collection.map((item) => item.fk_added_by))].map((id) => UsersService.getUser(id)),
      );

      setCreditTypes(creditTypes);
      setCredits(credits);
      setInitiators(initiators);
      setUser(user);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [brand, currency, currentPage, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Container maxWidth="xl">
      <UserBreadcrumbs
        userId={userId}
        user={user ? { label: user.fullName || user.email || 'User', href: `/users/${userId}` } : null}
        currentPage="Credits"
      />
      <CreditsCurrencySelector user={user} regions={regions} currency={currency} />

      {isInitialLoad && !user && <Spinner />}

      {error && <ErrorDisplay message={error} />}

      {!isInitialLoad && user && (
        <>
          <FraudWarning userId={userId} />
          <CreditsPage
            creditTypes={creditTypes}
            credits={credits}
            initiators={initiators}
            user={user}
            idUser={userId}
            currency={currency}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            isLoading={isLoading}
            rerender={fetchData}
          />
        </>
      )}
    </Container>
  );
}
